import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Th, Td } from "@/components/common";
import { api, errMsg, todayStr, qty } from "@/lib/api";

const EMPTY = { date: "", waybill_no: "", vehicle_plate: "", driver: "", address: "", notes: "" };

export default function ShipmentDialog({ open, onOpenChange, order, onSaved }) {
  const sale = order.kind === "sale";
  const [f, setF] = useState(EMPTY);
  const [lines, setLines] = useState([]);
  const [stock, setStock] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF({ ...EMPTY, date: todayStr() });
    api.get("/products").then((r) => {
      const s = Object.fromEntries(r.data.map((p) => [p.id, p.stock]));
      setStock(s);
      setLines(order.items.map((it) => {
        const rem = Math.max(it.qty - (it.shipped_qty || 0), 0);
        return sale ? Math.max(Math.min(rem, s[it.product_id] ?? 0), 0) : rem;
      }));
    }).catch((e) => toast.error(errMsg(e)));
  }, [open, order, sale]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    const items = lines.map((q, i) => ({ line_index: i, qty: Number(q) || 0 })).filter((l) => l.qty > 0);
    if (!items.length) return toast.error("En az bir satırda sevk miktarı girin");
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/shipments`, { ...f, items });
      toast.success(`${data.no} kaydedildi`);
      onOpenChange(false);
      onSaved?.();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl rounded-[4px]" data-testid="shipment-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{sale ? "Sevk Et" : "Mal Kabul"} · {order.no}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div className="max-h-[300px] overflow-auto border border-border">
            <table className="w-full">
              <thead><tr><Th>Ürün</Th><Th right>Sipariş</Th><Th right>Sevk Edilen</Th><Th right>Kalan</Th>{sale && <Th right>Stok</Th>}<Th right>{sale ? "Bu Sevk" : "Teslim Alınan"}</Th></tr></thead>
              <tbody>
                {order.items.map((it, i) => {
                  const rem = Math.max(it.qty - (it.shipped_qty || 0), 0);
                  const st = stock[it.product_id] ?? 0;
                  const q = Number(lines[i]) || 0;
                  return (
                    <tr key={i} className={rem <= 0 ? "opacity-50" : ""}>
                      <Td>{it.name}<div className="text-[11px] text-muted-foreground">{it.code}</div></Td>
                      <Td right>{qty(it.qty)}</Td>
                      <Td right>{qty(it.shipped_qty || 0)}</Td>
                      <Td right className="font-semibold">{qty(rem)}</Td>
                      {sale && <Td right className={st < rem ? "text-red-600" : ""}>{qty(st)}</Td>}
                      <Td right>
                        <Input type="number" step="any" min="0" max={rem} disabled={rem <= 0} value={lines[i] ?? 0}
                          onChange={(e) => setLines(lines.map((x, j) => (j === i ? e.target.value : x)))}
                          className="ml-auto w-24 text-right" data-testid={`shipment-qty-input-${i}`} />
                        {q > rem + 1e-9 && <div className="mt-1 text-[11px] text-red-600">Kalan en fazla {qty(rem)}</div>}
                        {sale && q > st && q <= rem && <div className="mt-1 text-[11px] text-red-600" data-testid={`shipment-stock-warning-${i}`}>Stok yetersiz</div>}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground">Kısmi sevk yapılırsa sevk edilen miktar siparişten düşer; kalan miktar siparişte açık kalır.</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Sevk Tarihi"><Input required type="date" value={f.date} onChange={set("date")} data-testid="shipment-date-input" /></Field>
            <Field label="İrsaliye No"><Input value={f.waybill_no} onChange={set("waybill_no")} data-testid="shipment-waybill-input" /></Field>
            <Field label="Araç Plakası"><Input value={f.vehicle_plate} onChange={set("vehicle_plate")} placeholder="34 ABC 123" data-testid="shipment-plate-input" /></Field>
            <Field label={sale ? "Şoför / Teslim Eden" : "Teslim Eden"}><Input value={f.driver} onChange={set("driver")} data-testid="shipment-driver-input" /></Field>
            <Field label={sale ? "Teslimat Adresi" : "Teslim Yeri"} className="sm:col-span-2"><Input value={f.address} onChange={set("address")} data-testid="shipment-address-input" /></Field>
            <Field label="Notlar" className="sm:col-span-3"><Input value={f.notes} onChange={set("notes")} data-testid="shipment-notes-input" /></Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="shipment-cancel-button">Vazgeç</Button>
            <Button type="submit" disabled={busy} data-testid="shipment-save-button">{busy ? "Kaydediliyor…" : "Kaydet"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
